import React, { useState, useEffect } from 'react';
// import './Managerdashboard.css';
import './Managerai.css';
import { FaClock, FaUsers, FaCheckCircle, FaCalendarAlt, FaUserTie } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { getBackendUrl } from '../../../api';
import Navbar1 from '../Navbar/Navbar1.jsx';
import Mainbar from '../Navbar/Mainbar.jsx';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, handleLogout, checkAuthStatus, canApproveLeaves, isManager } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    stats: {
      pendingCount: 0,
      teamMembersCount: 0,
      availableTodayCount: 0,
      thisMonthLeaves: 0
    },
    teamMembers: [],
    upcomingLeaves: [],
    pendingApprovals: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ NEW: Store team member IDs for badge checking
  const [teamMemberIds, setTeamMemberIds] = useState([]);
  const [teamMemberEmails, setTeamMemberEmails] = useState([]); // Also track by email

  const getUserInitials = (name, email) => {
    if (name && name.trim()) {
      return name.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'M';
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    
    try {
      const cleanDateStr = dateStr.toString().trim();
      
      if (cleanDateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const [day, month, year] = cleanDateStr.split('/').map(Number);
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year > 1900) {
          const date = new Date(year, month - 1, day);
          if (date.getDate() === day && date.getMonth() === month - 1) {
            return date;
          }
        }
      }
      
      if (cleanDateStr.match(/^\d{4}-\d{1,2}-\d{1,2}/)) {
        const date = new Date(cleanDateStr + 'T00:00:00');
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      
      const date = new Date(cleanDateStr);
      if (!isNaN(date.getTime()) && date.getFullYear() > 1900) {
        return date;
      }
      
      console.warn('Could not parse date:', cleanDateStr);
      return null;
      
    } catch (error) {
      console.error('Date parsing error:', dateStr, error);
      return null;
    }
  };

  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return 'Invalid Date';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Date not specified';
    
    const start = typeof startDate === 'string' ? parseDateString(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseDateString(endDate) : endDate;
    
    if (!start) return 'Invalid date';
    
    const finalEnd = end || start;
    
    if (start.toDateString() === finalEnd.toDateString()) {
      return formatDate(start);
    }
    
    return `${formatDate(start)} - ${formatDate(finalEnd)}`;
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'approved':
        return 'status-approved';
      case 'pending':
        return 'status-pending';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const formatLeaveTypeForDisplay = (leaveType) => {
    const typeMap = {
      'casual': 'Casual Leave',
      'sick': 'Medical Leave',
      'medical': 'Medical Leave',
      'emergency': 'Permission',
      'permission': 'Permission',
      'annual': 'Annual Leave',
      'maternity': 'Maternity Leave',
      'paternity': 'Paternity Leave',
      'unpaid': 'Unpaid Leave'
    };
    return typeMap[leaveType?.toLowerCase()] || leaveType || 'Unknown';
  };

  const isHourBasedLeaveType = (leaveType) => {
    const hourBasedTypes = ['permission', 'emergency'];
    return hourBasedTypes.includes(leaveType?.toLowerCase());
  };

  const formatDuration = (leaveType, daysRequested) => {
    const isHourBased = isHourBasedLeaveType(leaveType);
    
    if (isHourBased) {
      const hours = daysRequested || 1;
      return hours === 1 ? '1 hour' : `${hours} hours`;
    } else {
      const days = daysRequested || 1;
      return days === 1 ? '1 day' : `${days} days`;
    }
  };

  // ✅ FETCH ALL EMPLOYEES UPCOMING LEAVES
  const fetchUpcomingLeaves = async () => {
    try {
      console.log('📅 ===== FETCHING ALL EMPLOYEES UPCOMING LEAVES =====');
      console.log('🔍 Current teamMemberIds:', teamMemberIds);
      console.log('🔍 Current teamMemberEmails:', teamMemberEmails);
      
      const backendUrl = getBackendUrl();
      console.log('Backend URL:', backendUrl);
      
      try {
        console.log('Method 1: Trying /api/all-employees/upcoming-leaves...');
        const response = await fetch(`${backendUrl}/api/all-employees/upcoming-leaves`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        console.log('Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Raw response data:', data);
          
          const upcomingLeavesData = data.upcoming_leaves || data || [];
          console.log(`✅ Method 1 SUCCESS: Got ${upcomingLeavesData.length} leaves`);
          
          if (upcomingLeavesData.length > 0) {
            console.log('Sample leave data:', upcomingLeavesData[0]);
            
            const formattedLeaves = upcomingLeavesData.map(leave => {
              const leaveType = leave.leaveType || leave.leave_type || 'Casual Leave';
              const isHourBased = leave.isHourBased || isHourBasedLeaveType(leaveType);
              
              // ✅ CRITICAL FIX: Check by BOTH ID and EMAIL
              const employeeId = leave.user_id || leave.employeeId || leave.employee_id;
              const employeeEmail = (leave.employeeEmail || leave.employee_email || '').toLowerCase();
              
              const isTeamMemberById = teamMemberIds.includes(employeeId);
              const isTeamMemberByEmail = teamMemberEmails.includes(employeeEmail);
              const isTeamMember = isTeamMemberById || isTeamMemberByEmail;
              
              console.log(`🔍 Checking leave for ${leave.employeeName}:`, {
                employeeId,
                employeeEmail,
                isTeamMemberById,
                isTeamMemberByEmail,
                finalIsTeamMember: isTeamMember
              });
              
              return {
                id: leave.id,
                employeeName: leave.employeeName || leave.employee_name,
                employeeRole: leave.employeeRole || leave.employee_role || 'Employee',
                employeeEmail: leave.employeeEmail || leave.employee_email || '',
                employeeId: employeeId,
                leaveType: leaveType,
                leaveTypeRaw: leaveType,
                duration: leave.duration || formatDuration(leaveType, leave.daysRequested || leave.days_requested || 1),
                dateRange: leave.dateRange || leave.date_range || formatDateRange(leave.startDate || leave.start_date, leave.endDate || leave.end_date),
                status: leave.status || 'Approved',
                reason: leave.reason || 'No reason provided',
                statusClass: getStatusBadgeClass(leave.status || 'Approved'),
                avatar: getUserInitials(leave.employeeName || leave.employee_name, leave.employeeEmail || leave.employee_email),
                daysRequested: leave.daysRequested || leave.days_requested || 1,
                startDate: leave.startDate || leave.start_date,
                startDateISO: leave.startDateISO || leave.start_date_iso,
                endDate: leave.endDate || leave.end_date,
                endDateISO: leave.endDateISO || leave.end_date_iso,
                isHourBased: isHourBased,
                allEmployees: true,
                isTeamMember: isTeamMember // ✅ FIXED: Now checks both ID and email
              };
            });
            
            console.log(`✅ Formatted ${formattedLeaves.length} leaves for display`);
            console.log('Team member flags:', formattedLeaves.map(l => ({name: l.employeeName, isTeamMember: l.isTeamMember})));
            return formattedLeaves;
          }
        } else {
          console.warn('Method 1 failed with status:', response.status);
        }
      } catch (method1Error) {
        console.error('Method 1 error:', method1Error);
      }
      
      console.log('Method 2: Trying /api/manager/upcoming-leaves...');
      try {
        const response = await fetch(`${backendUrl}/api/manager/upcoming-leaves`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const upcomingLeavesData = await response.json();
          console.log(`✅ Method 2 SUCCESS: Got ${upcomingLeavesData.length} leaves from manager endpoint`);
          
          if (upcomingLeavesData.length > 0) {
            const formattedLeaves = upcomingLeavesData.map(leave => {
              const leaveType = leave.leaveType || leave.leave_type || 'Casual Leave';
              const isHourBased = leave.isHourBased || isHourBasedLeaveType(leaveType);
              
              // ✅ Manager endpoint - all are team members by definition
              const isTeamMember = true;
              
              console.log(`✅ Manager endpoint leave: ${leave.employeeName} - isTeamMember: true`);
              
              return {
                id: leave.id,
                employeeName: leave.employeeName,
                employeeRole: leave.employeeRole || 'Employee',
                employeeEmail: leave.employeeEmail || leave.employee_email || '',
                employeeId: leave.user_id || leave.employeeId,
                leaveType: leaveType,
                leaveTypeRaw: leaveType,
                duration: leave.duration || formatDuration(leaveType, leave.daysRequested || leave.days_requested || 1),
                dateRange: leave.dateRange || formatDateRange(leave.startDate || leave.start_date, leave.endDate || leave.end_date),
                status: leave.status || 'Approved',
                reason: leave.reason || 'No reason provided',
                statusClass: getStatusBadgeClass(leave.status || 'Approved'),
                avatar: getUserInitials(leave.employeeName, leave.employeeEmail || leave.employee_email),
                daysRequested: leave.daysRequested || leave.days_requested || 1,
                startDate: leave.startDate || leave.start_date,
                startDateISO: leave.startDateISO,
                endDate: leave.endDate || leave.end_date,
                endDateISO: leave.endDateISO,
                isHourBased: isHourBased,
                allEmployees: false,
                isTeamMember: isTeamMember // ✅ All from manager endpoint are team members
              };
            });
            
            console.log(`✅ Formatted ${formattedLeaves.length} leaves from manager endpoint`);
            return formattedLeaves;
          }
        }
      } catch (method2Error) {
        console.error('Method 2 error:', method2Error);
      }
      
      console.error('❌ All methods failed - returning empty array');
      return [];
      
    } catch (error) {
      console.error('❌ Critical error fetching upcoming leaves:', error);
      return [];
    }
  };

  // ✅ FETCH TEAM MEMBERS (with ID and EMAIL tracking)
  const fetchTeamMembers = async () => {
    try {
      console.log('👥 ===== FETCHING TEAM MEMBERS =====');
      console.log(`Manager ID: ${user?.id}`);
      console.log(`Manager Name: ${user?.name}`);
      
      const backendUrl = getBackendUrl();
      
      const response = await fetch(`${backendUrl}/api/employee-list`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const allEmployees = await response.json();
        console.log(`📋 Total employees in system: ${allEmployees.length}`);
        
        const assignedTeamMembers = allEmployees.filter(employee => {
          const isAssignedToManager = employee.manager_id === user?.id;
          const isNotManager = employee.id !== user?.id;
          
          if (isAssignedToManager && isNotManager) {
            console.log(`✅ Team Member: ${employee.name} (${employee.email}) - ID: ${employee.id}`);
            return true;
          }
          
          if (employee.id === user?.id) {
            console.log(`⭐ Excluded: ${employee.name} (manager themselves)`);
          }
          
          return false;
        });
        
        console.log(`\n✅ Found ${assignedTeamMembers.length} team members assigned to this manager`);
        
        // ✅ CRITICAL FIX: Store BOTH IDs and EMAILS for matching
        const memberIds = assignedTeamMembers.map(member => member.id);
        const memberEmails = assignedTeamMembers.map(member => (member.email || '').toLowerCase());
        
        console.log('✅ Team member IDs stored:', memberIds);
        console.log('✅ Team member emails stored:', memberEmails);
        
        // Update state immediately
        setTeamMemberIds(memberIds);
        setTeamMemberEmails(memberEmails);
        
        const processedTeamMembers = assignedTeamMembers.map(member => {
          console.log(`\n📄 Processing: ${member.name} (ID: ${member.id})`);
          
          const hasFullDayLeaveToday = member.on_leave_today || false;
          const hasHourBasedLeaveToday = false;
          
          let status, period;
          
          if (hasFullDayLeaveToday) {
            status = 'on-leave';
            period = 'On Leave Today';
          } else if (hasHourBasedLeaveToday) {
            status = 'partial-leave';
            period = 'Partial Leave (Hours)';
          } else {
            status = 'available';
            period = 'Available';
          }
          
          console.log(`   Status: ${status} - ${period}`);
          
          return {
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role || 'Employee',
            status: status,
            avatar: getUserInitials(member.name, member.email),
            period: period,
            totalRequests: member.total_requests || 0,
            approvedLeaves: member.approved_leaves || 0,
            pendingLeaves: member.pending_leaves || 0,
            attendanceStatus: hasFullDayLeaveToday ? 'On Leave' : 'Present',
            assignedToCurrentManager: true,
            hasHourBasedLeaveToday: hasHourBasedLeaveToday,
            hasFullDayLeaveToday: hasFullDayLeaveToday
          };
        });
        
        console.log('\n📊 Team Processing Complete:');
        console.log(`   Total Team Members: ${processedTeamMembers.length}`);
        
        return {
          teamMembers: processedTeamMembers,
          totalTeamSize: processedTeamMembers.length,
          memberIds: memberIds, // Return for immediate use
          memberEmails: memberEmails // Return for immediate use
        };
      } else {
        console.error('❌ Failed to fetch employee list');
        return { teamMembers: [], totalTeamSize: 0, memberIds: [], memberEmails: [] };
      }
    } catch (error) {
      console.error('❌ Error fetching team members:', error);
      return { teamMembers: [], totalTeamSize: 0, memberIds: [], memberEmails: [] };
    }
  };

  const fetchManagerStats = async (teamMembersData) => {
    try {
      console.log('📊 Fetching manager stats from BACKEND...');
      const backendUrl = getBackendUrl();
      
      const pendingApprovalsResponse = await fetch(`${backendUrl}/api/pending-approvals`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      let pendingCount = 0;
      if (pendingApprovalsResponse.ok) {
        const pendingApprovals = await pendingApprovalsResponse.json();
        pendingCount = Array.isArray(pendingApprovals) ? pendingApprovals.length : 0;
        console.log(`✅ Found ${pendingCount} pending approvals`);
      }
      
      const statsResponse = await fetch(`${backendUrl}/api/manager-stats`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      let thisMonthLeaves = 0;
      
      if (statsResponse.ok) {
        const backendStats = await statsResponse.json();
        console.log('✅ Backend stats received:', backendStats);
        
        thisMonthLeaves = backendStats.approvedThisMonth || 0;
        
        console.log(`📊 This Month Leave Days from Backend: ${thisMonthLeaves}`);
      } else {
        console.warn('⚠️ Could not fetch manager-stats from backend, will show 0');
      }
      
      console.log('📊 Final Manager Stats:', {
        pendingCount,
        thisMonthLeaves,
        teamMembersCount: teamMembersData.length
      });
      
      return {
        pendingCount,
        approvedThisMonth: thisMonthLeaves,
        rejectedThisMonth: 0,
        upcomingLeavesCount: 0,
        totalRequests: 0,
        teamBasedStats: true
      };
      
    } catch (error) {
      console.error('❌ Error fetching manager stats:', error);
      return {
        pendingCount: 0,
        approvedThisMonth: 0,
        rejectedThisMonth: 0,
        upcomingLeavesCount: 0,
        totalRequests: 0,
        teamBasedStats: true
      };
    }
  };

  const fetchDashboardData = async () => {
    try {
      console.log('=== FETCHING ALL ASSIGNED TEAM MEMBERS ===');
      console.log('Manager ID:', user?.id);
      console.log('Manager email:', user?.email);
      
      // ✅ CRITICAL: Fetch team members FIRST to get team member IDs and EMAILS
      const teamData = await fetchTeamMembers();
      console.log('Raw team data received:', teamData);
      console.log('Team members count:', teamData.teamMembers.length);
      
      // ✅ IMPORTANT: Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 100));

      const statsData = await fetchManagerStats(teamData.teamMembers);
      
      // ✅ NOW fetch upcoming leaves (will use stored teamMemberIds and teamMemberEmails)
      console.log('📅 About to fetch upcoming leaves with team member tracking');
      console.log('   - Team member IDs:', teamData.memberIds);
      console.log('   - Team member emails:', teamData.memberEmails);
      
      const upcomingLeavesData = await fetchUpcomingLeaves();
      console.log('📅 Upcoming leaves fetched:', upcomingLeavesData.length);
      console.log('   - Leaves with team member flag:', upcomingLeavesData.filter(l => l.isTeamMember).length);

      const availableTodayCount = teamData.teamMembers.filter(member => {
        const isAvailable = member.status === 'available' || member.status === 'partial-leave';
        console.log(`Availability: ${member.name} - ${member.status} - ${isAvailable}`);
        return isAvailable;
      }).length;

      console.log('FINAL STATS:');
      console.log(`   Team Size: ${teamData.teamMembers.length}`);
      console.log(`   Available Today: ${availableTodayCount}`);
      console.log(`   Pending Requests: ${statsData.pendingCount}`);
      console.log(`   This Month Leave Days: ${statsData.approvedThisMonth}`);
      console.log(`   Upcoming Leaves: ${upcomingLeavesData.length}`);
      console.log(`   Team Member Leaves: ${upcomingLeavesData.filter(l => l.isTeamMember).length}`);

      const newDashboardData = {
        stats: {
          pendingCount: statsData.pendingCount,
          teamMembersCount: teamData.teamMembers.length,
          availableTodayCount: availableTodayCount,
          thisMonthLeaves: statsData.approvedThisMonth
        },
        teamMembers: teamData.teamMembers,
        upcomingLeaves: upcomingLeavesData
      };

      console.log('=== COMPLETE TEAM DATA LOADED ===');
      console.log('Final Stats:', newDashboardData.stats);
      console.log('Upcoming Leaves Count:', newDashboardData.upcomingLeaves.length);

      setDashboardData(newDashboardData);
      setError(null);

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setError('Failed to load dashboard data. Please refresh the page.');
    }
  };

  useEffect(() => {
    const init = async () => {
      console.log('Initializing manager dashboard...');
      setLoading(true);
      
      try {
        await checkAuthStatus();
        
        if (user) {
          if (!isManager() && !canApproveLeaves) {
            setError('Access denied. Manager privileges required.');
            return;
          }
          
          console.log('Current manager:', {
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role
          });
          
          await fetchDashboardData();
        }
      } catch (error) {
        console.error('Initialization error:', error);
        setError('Failed to initialize dashboard');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [user, checkAuthStatus, isManager, canApproveLeaves]);

  useEffect(() => {
    if (user && isManager()) {
      const interval = setInterval(() => {
        console.log('Auto-refreshing dashboard...');
        fetchDashboardData();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user, isManager]);

  const handleViewLeaveDetails = (leave) => {
    const durationText = leave.isHourBased 
      ? `Duration: ${leave.duration} (Hour-based permission)`
      : `Duration: ${leave.duration}`;
      
    alert(`Leave Details:

Employee: ${leave.employeeName}
Role: ${leave.employeeRole}
Email: ${leave.employeeEmail || 'Not provided'}
Leave Type: ${leave.leaveType}
${durationText}
Dates: ${leave.dateRange}
Status: ${leave.status}
Reason: ${leave.reason}`);
  };

  const UpcomingLeavesTable = ({ records, loading, error }) => {
    console.log('UpcomingLeavesTable render:', {
      recordsCount: records?.length || 0,
      loading,
      error,
      sampleRecord: records?.[0]
    });

    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading upcoming leaves...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <p className="error-message">⚠️ {error}</p>
          <button onClick={fetchDashboardData} className="retry-btn">
            🔄 Retry
          </button>
        </div>
      );
    }

    if (!records || records.length === 0) {
      return (
        <div className="no-data-container">
          <div className="no-data-message"></div>
          <div className="no-data-subtitle">
            Future approved leave requests will appear here.<br/>
            Currently showing: {records ? '0 records (empty array)' : 'null/undefined'}
          </div>
          <button 
            onClick={fetchDashboardData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      );
    }

    console.log(`Rendering table with ${records.length} records`);

    return (
      <table className="upcoming-leaves-table leave-table" style={{overflowX:'auto'}} >
        <thead>
          <tr>
            <th>EMPLOYEE</th>
            <th>LEAVE TYPE</th>
            <th>DURATION</th>
            <th>STATUS</th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {records.map((leave, index) => {
            console.log(`Rendering row ${index}:`, leave.employeeName, leave.leaveType, 'isTeamMember:', leave.isTeamMember);
            
            return (
              <tr key={leave.id || index}>
                <td>
                  <div className="employee-name">{leave.employeeName}</div>
                  <div className="manager-name">
                    {leave.employeeRole} • {leave.employeeEmail}
                  </div>
                  {/* ✅ CRITICAL: Show Team Member badge if assigned to manager */}
                  {leave.isTeamMember && (
                    <div style={{
                      fontSize: '11px',
                      color: '#059669',
                      backgroundColor: '#dcfce7',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      marginTop: '4px',
                      display: 'inline-block'
                    }}>
                      Team Member
                    </div>
                  )}
                </td>
                <td>
                  <span className={`leave-type ${leave.isHourBased ? 'hour-based' : 'day-based'}`}>
                    {leave.leaveType}
                    {leave.isHourBased && (
                      <span className="hour-indicator" title="Hour-based leave">⏱</span>
                    )}
                  </span>
                </td>
                <td>
                  <div className={`duration-display ${leave.isHourBased ? 'hour-duration' : 'day-duration'}`}>
                    {leave.duration}
                    {leave.isHourBased && (
                      <div className="duration-note">Hour-based</div>
                    )}
                  </div>
                  <div className="date-range">{leave.dateRange}</div>
                </td>
                <td>
                  <span className={`status-badge ${leave.statusClass || getStatusBadgeClass(leave.status)}`}>
                    {leave.status}
                  </span>
                </td>
                <td>
                  <button 
                    className="view-btn"
                    onClick={() => handleViewLeaveDetails(leave)}
                    title="View Details"
                  >
                    👁 View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const cardData = [
    {
      title: "Team Pending Approvals",
      titleColor: "#2196F3",
      value: dashboardData.stats.pendingCount.toString(),
      icon: <img src="/Images/Timesheet1.png" alt="Pending" />,
      trend: dashboardData.stats.pendingCount > 0 
        ? `${dashboardData.stats.pendingCount} awaiting approval` 
        : "No pending requests"
    },
    {
      title: "Complete Team Size",
      value: dashboardData.stats.teamMembersCount.toString(),
      icon: <img src="/Images/Activeemp.png" alt="Team" />,
      trend: `ALL ${dashboardData.stats.teamMembersCount} assigned members`
    },
    {
      title: "Available Today",
      value: dashboardData.stats.availableTodayCount.toString(),
      icon: <img src="/Images/redtick.png" alt="Available" />,
      trend: `${dashboardData.stats.availableTodayCount} of ${dashboardData.stats.teamMembersCount} available`
    },
    {
      title: "This Month Leaves",
      value: dashboardData.stats.thisMonthLeaves.toString(),
      icon: <img src="/Images/orangecalend.png" alt="Calendar" />,
      trend: dashboardData.stats.thisMonthLeaves > 0 
        ? `${dashboardData.stats.thisMonthLeaves} leave day${dashboardData.stats.thisMonthLeaves !== 1 ? 's' : ''} this month` 
        : "No leaves this month"
    }
  ];

  const quickActions = [
    {
      icon: <img src="/Images/teamrequest.png" alt="⏰" />,
      title: 'Review Team Requests',
      badge: dashboardData.stats.pendingCount,
      color: 'yellow',
      onClick: () => navigate("/pendingapprovals")
    },
    {
      icon: <img src="/Images/teamoverview.png" alt="👥" />,
      title: 'Complete Team Overview',
      color: 'blue',
      onClick: () => navigate("/team")
    },
    {
      icon: <img src="/Images/calender1.png" alt="📅" />,
      title: 'Team Calendar',
      color: 'green',
      onClick: () => navigate("/calender")
    },
    {
      icon: <img src="/Images/team-analytics.png" alt="📊" />,
      title: 'Team Analytics',
      color: 'purple',
      onClick: () => navigate("/team")
    }
  ];

  // if (loading) {
  //   return (
  //     <div className="manager-portal">
  //       <div className="loading-full-screen">
  //         <div className="loading-spinner-large"></div>
  //         <div className="loading-text">Loading Manager Dashboard...</div>
  //       </div>
  //     </div>
  //   );
  // }

  if (!user) {
    return (
      <div className="manager-portal">
        <div className="auth-required">
          <div className="auth-icon">🔒</div>
          <h2>Authentication Required</h2>
          <p>Please log in to access the Manager Dashboard</p>
          <button onClick={() => navigate('/')} className="login-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!isManager() && !canApproveLeaves) {
    return (
      <div className="manager-portal">
        <div className="auth-required">
          <div className="auth-icon">⚠️</div>
          <h2>Access Denied</h2>
          <p>Manager privileges required to access this dashboard</p>
          <p>Current role: {user?.role || 'Unknown'}</p>
          <button onClick={() => navigate('/Worker')} className="login-btn">
            Go to Worker Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-portal">
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .loading-full-screen {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          flex-direction: column;
          gap: 20px;
        }
        
        .loading-spinner-large {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #1976d2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .loading-text {
          font-size: 18px;
          color: #666;
        }
        
        .auth-required {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          flex-direction: column;
          gap: 20px;
          padding: 20px;
          text-align: center;
        }
        
        .auth-icon {
          font-size: 48px;
          margin-bottom: 20px;
        }
        
        .auth-required h2 {
          margin: 0 0 10px 0;
          color: #333;
        }
        
        .auth-required p {
          color: #666;
          margin-bottom: 30px;
        }
        
        .login-btn {
          padding: 12px 24px;
          background-color: #1976d2;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
        }
        
        .loading-container, .error-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px 20px;
          flex-direction: column;
          gap: 15px;
        }
        
        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #1976d2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .error-message {
          color: #dc3545;
          font-weight: 500;
        }
        
        .retry-btn {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }
        
        .no-data-container {
          text-align: center;
          padding: 40px 20px;
          color: #64748b;
        }
        
        .no-data-message {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #374151;
        }
        
        .no-data-subtitle {
          font-size: 14px;
          line-height: 1.5;
          color: #6b7280;
          margin-bottom: 20px;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-block;
        }
        
        .status-approved {
          background-color: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }
        
        .status-pending {
          background-color: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        }
        
        .status-rejected {
          background-color: #fecaca;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }
        
        .upcoming-leaves-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .upcoming-leaves-table th {
          background-color: #f8fafc;
          color: #475569;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .upcoming-leaves-table td {
          padding: 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 14px;
          vertical-align: top;
        }
        
        .upcoming-leaves-table tr:hover {
          background-color: #f8fafc;
        }
        
        .leave-type {
          background-color: #e0f2fe;
          color: #0277bd;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        
        .leave-type.hour-based {
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        }
        
        .leave-type.day-based {
          background-color: #e0f2fe;
          color: #0277bd;
        }
        
        .hour-indicator {
          font-size: 10px;
          opacity: 0.8;
        }
        
        .duration-display {
          font-weight: 500;
        }
        
        .duration-display.hour-duration {
          color: #856404;
        }
        
        .duration-display.day-duration {
          color: #0277bd;
        }
        
        .duration-note {
          font-size: 10px;
          color: #856404;
          font-style: italic;
          margin-top: 2px;
        }
        
        .date-range {
          font-size: 12px;
          color: #64748b;
          margin-top: 4px;
        }
        
        .view-btn {
          background-color: #3b82f6;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .view-btn:hover {
          background-color: #2563eb;
        }
        
        .employee-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        
        .manager-name {
          font-size: 12px;
          color: #64748b;
        }
      `}</style>

      <main className="main-content">
        <div className="mdmanager-navbar managerdashboard-navbar"><Navbar1/></div>
        <div className="container" >
          <div className="mdmainbar" ><Mainbar/></div>
          {/* <div className="mainbar"><Mainbar/></div> */}
          <div className="dashboard-header">
            <h2 className="dashboard-title" style={{color:'#2196F3'}}>Complete Team Manager Dashboard</h2>
            <p className="dashboard-subtitle">
              Welcome back, {user?.name || 'Manager'}! Managing ALL {dashboardData.stats.teamMembersCount} assigned team members.
            </p>
            
            {error && (
              <div style={{
                color: '#ef4444',
                marginTop: '10px',
                padding: '12px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>⚠️ {error}</span>
                <button
                  onClick={fetchDashboardData}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          <div className="stats-grid">
            <div className="dashboard-wrapper">
              {cardData.map((card, index) => (
                <div className="dashboard-card" key={index}>
                  <div className="card-left">
                    <div className="card-title">{card.title}</div>
                    <div className="card-value">{card.value}</div>
                    {card.trend && (
                      <div className="card-trend">{card.trend}</div>
                    )}
                  </div>
                  <div
                    className="card-icon"
                    style={{ backgroundColor: card.iconColor }}
                  >
                    {card.icon}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="content-grid">
            <div className="full-width-column">
              <div className="card card-first">  
                <div className="quick-actions-grid">
                {/* <div className="Admin-Dashboard-actions-grid "> */}
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      className={`quick-action-btn ${action.color}`}
                      onClick={action.onClick} 
                    >
                      <div className={`action-icon ${action.color}`}>
                        <span>{action.icon}</span>
                      </div>
                      <span className="action-title">{action.title}</span>
                      {action.badge > 0 && (
                        <div className="action-badge">
                          <span>{action.badge}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="card  card-two" style={{width:'100%'}} >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 className="card-title">
                    All Team Upcoming Leaves ({dashboardData.upcomingLeaves.length})
                  </h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ 
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#856404'
                      }}></span>
                      Hours
                      <span style={{ 
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#0277bd',
                        marginLeft: '8px'
                      }}></span>
                      Days
                    </div>
                    <button
                      onClick={fetchDashboardData}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>
                </div>
                
                <div className='manager-upcoming-leaves'  style={{ overflowX: 'scroll' }}>
                  <UpcomingLeavesTable 
                    records={dashboardData.upcomingLeaves}
                    loading={loading}
                    error={error}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;